import React, { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener, Paper } from '@material-ui/core';
import { pull, uniq } from 'lodash';

import {
  BatchUpdateUserProfileStatusMutation,
  BatchUpdateUserProfileTypeMutation,
  GetUserSearchesV2QueryVariables,
  SearchSortFieldsV2,
  useBatchUpdateUserProfileStatusMutation,
  useBatchUpdateUserProfileTypeMutation,
  UserStatus,
  UserType,
} from 'api';
import { EmptyState, Search } from 'components';
import { useNotify } from 'hooks';
import { useGetUserSearchesV2 } from 'features/admin/hooks';
import { useBatchUpdateUserStatus, useBatchUpdateUserType } from 'features/admin/updaters';

import { Header, Tabs, Table, Total } from '../components/People';

const LIMIT = 24;

const People: FC = () => {
  // Data State
  const [filter, setFilter] = useState(UserStatus.Active);
  const [checkedIds, setCheckedIds] = useState([]);

  // UI State
  const [term, setTerm] = useState('');
  const [focused, setFocused] = useState(false);
  const [allChecked, setAllChecked] = useState(false);

  const { t } = useTranslation();
  const notify = useNotify();

  const variables: GetUserSearchesV2QueryVariables = {
    user_status: filter,
    q: term,
    size: LIMIT,
    sort: SearchSortFieldsV2.FullName,
    next_id: '',
  };

  const updateUserStatusMutationOptions = useBatchUpdateUserStatus(variables);
  const updateUserTypeMutationOptions = useBatchUpdateUserType(variables);

  const { count, results, sentinelRef, isEmpty, isExhausted, isInitialLoad } =
    useGetUserSearchesV2(variables);

  const { mutate: updateUserProfileStatus } = useBatchUpdateUserProfileStatusMutation({
    onSuccess: onUpdateStatusSuccess,
    ...updateUserStatusMutationOptions,
  });

  const { mutate: updateUserProfileType } = useBatchUpdateUserProfileTypeMutation({
    onSuccess: onUpdateTypeSuccess,
    ...updateUserTypeMutationOptions,
  });

  function handleUpdateStatus(userStatus: UserStatus) {
    updateUserProfileStatus({
      ids: checkedIds,
      user_status: userStatus,
    });
  }

  function handleUpdateType(userType: UserType) {
    updateUserProfileType({
      ids: checkedIds,
      user_type: userType,
    });
  }

  function onUpdateStatusSuccess(mutation: BatchUpdateUserProfileStatusMutation) {
    const { batchUpdateUserProfileStatus } = mutation;
    const userStatus = batchUpdateUserProfileStatus[0].user_status;
    const isSuspending = userStatus === UserStatus.Disabled;
    const statusMessage = isSuspending ? t('admin:suspended') : t('admin:activated');

    const notifyOptions = {
      i18n: {
        i18nKey: 'admin:userStatusSuccessMessage',
        count: checkedIds.length,
        values: { count: checkedIds.length, status: statusMessage },
      },
    };

    notify.info(notifyOptions);
    setCheckedIds([]);
    setAllChecked(false);
  }

  function onUpdateTypeSuccess(mutation: BatchUpdateUserProfileTypeMutation) {
    const { batchUpdateUserProfileUserType } = mutation;
    const userType = batchUpdateUserProfileUserType[0].user_type;

    const notifyOptions = {
      i18n: {
        i18nKey: 'admin:userTypeSuccessMessage',
        count: checkedIds.length,
        values: { count: checkedIds.length, userType: t(`common:${userType}`) },
      },
    };

    notify.info(notifyOptions);
    setCheckedIds([]);
    setAllChecked(false);
  }

  function handleReset(value: UserStatus) {
    setFilter(value);
    setCheckedIds([]);

    setAllChecked(false);
    setTerm('');
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    setTerm(value);

    if (!!term === !value) {
      setCheckedIds([]);
      setAllChecked(false);
    }
  }

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const ids = checked ? results.map(result => result.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function renderPeopleTable() {
    if (isEmpty) {
      return <EmptyState name="suspendedUsers" />;
    }

    return (
      <Table
        results={results}
        sentinelRef={sentinelRef}
        isInitialLoad={isInitialLoad}
        isAllChecked={allChecked}
        isExhausted={isExhausted}
        checkedIds={checkedIds}
        onAllChecked={handleAllChecked}
        onChecked={handleChecked}
      />
    );
  }

  return (
    <Paper>
      <Header
        filter={filter}
        count={checkedIds.length || count}
        onUpdateStatus={!!checkedIds.length && handleUpdateStatus}
        onUpdateType={!!checkedIds.length && handleUpdateType}
        tabs={<Tabs filter={filter} onChange={handleReset} />}
        total={<Total total={checkedIds.length || count} />}
        search={
          <ClickAwayListener onClickAway={() => setFocused(false)}>
            <Search
              query={term}
              focused={focused}
              onChange={handleSearchChange}
              onClear={() => handleReset(UserStatus.Active)}
              onFocus={() => setFocused(true)}
            />
          </ClickAwayListener>
        }
      />
      {renderPeopleTable()}
    </Paper>
  );
};

export default People;
